import {
  SearchResultSchema as PrismaSearchResultSchema,
  SearchTargetMetadataSchema,
  SortOrderSchema,
} from '@retro-ui/prisma';
import { z, ZodSchema } from 'zod';

export enum CompositeQueryOperator {
  AND = 'AND',
  OR = 'OR',
  NOT = 'NOT',
}

export type SortOrder = z.infer<typeof SortOrderSchema>;

export const CompositeQueryOperatorSchema = z.nativeEnum(CompositeQueryOperator);
export type CompositeQueryOperatorType = keyof typeof CompositeQueryOperator;

export const PaginationSchema = z.object({
  skip: z.coerce.number().optional(),
  take: z.coerce.number().optional(),
});

export type PaginationSchemaType = z.infer<typeof PaginationSchema>;

export function buildOrderBySchema<T extends z.ZodType<string>>(keys: T) {
  return z.array(z.record(keys, SortOrderSchema));
}

export function withPaginationSchema(dataSchema: ZodSchema) {
  return z.object({
    pagination: z.object({
      total: z.number(),
    }),
    data: dataSchema,
  });
}

export const ReactionSchema = z.object({
  productSmiles: z.string(),
  substrateSmiles: z.array(z.string()),
  productToMap: z.array(z.number()),
  substratesToMap: z.array(z.array(z.number())),
});

export type Reaction = z.infer<typeof ReactionSchema>;

export const PublicSearchResultSchema = PrismaSearchResultSchema.pick({
  id: true,
  batchId: true,
  status: true,
  result: true,
  reactionCount: true,
  referenceCount: true,
  price: true,
  timedOut: true,
  startedAt: true,
  finishedAt: true,
});
export type PublicSearchResult = z.infer<typeof PublicSearchResultSchema>;

export const SearchResultCommonMetadataSchema = z.object({
  runningTime: z.number().nullable(),
  targetMetadata: SearchTargetMetadataSchema.pick({ name: true }).nullable(),
});
export type SearchResultCommonMetadata = z.infer<typeof SearchResultCommonMetadataSchema>;

export const SearchResultSchema = PrismaSearchResultSchema.omit({
  searchGraph: true,
  metadata: true,
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

export const SearchResultWithMetadataSchema = SearchResultSchema.merge(SearchResultCommonMetadataSchema);
export type SearchResultWithMetadata = z.infer<typeof SearchResultWithMetadataSchema>;

export const SearchResultInListSchema = SearchResultSchema.omit({
  synthesis: true,
  decomposition: true,
  targetMolfile: true,
  sciFinderId: true,
  certainty: true,
});
export type SearchResultInList = z.infer<typeof SearchResultInListSchema>;

export const ExportedSearchResultInListSchema = PrismaSearchResultSchema.extend({
  name: z.string(),
})
  .merge(SearchResultCommonMetadataSchema)
  .partial();

export type ExportedSearchResultInList = z.infer<typeof ExportedSearchResultInListSchema>;

export const MolSchema = z.object({
  defaultAtomPickIdx: z.string().optional(),
  defaultBondPickIdx: z.string().optional(),
  atoms: z.record(
    z.object({
      idx: z.string(),
      element: z.string(),
      charge: z.number(),
      chirality: z.string().optional(),
    })
  ),
  bonds: z.record(
    z.object({
      idx: z.string(),
      type: z.union([z.literal('dashed'), z.literal('wedge')]),
      start: z.string(),
      end: z.string(),
      order: z.number(),
      geometry: z.any(),
    })
  ),
  nextAtom: z.number(),
  nextBond: z.number(),
  positions: z.record(z.array(z.number())),
  key: z.string().optional(),
});
export type Mol = z.infer<typeof MolSchema>;

export const VectorSchema = z.tuple([z.number(), z.number(), z.number()]);
export type Vector = z.infer<typeof VectorSchema>;

const BoundingBoxSchema = z.object({
  max: VectorSchema,
  min: VectorSchema,
  size: VectorSchema.optional(),
});

const DrawnAtomTextPartSchema = z.object({
  shift: VectorSchema,
  size: VectorSchema,
  text: z.string(),
  pivot: z.boolean(),
  type: z.string().optional(),
});

const DrawnAtomDataSchema = z.object({
  explicitValence: z.number(),
  valence: z.number(),
  hydrogens: z.number(),
});

const DrawnAtomCenterLabelSchema = z.object({
  parts: z.array(DrawnAtomTextPartSchema),
  bbox: BoundingBoxSchema,
});

const DrawnAtomSideLabelSchema = z.object({
  parts: z.array(DrawnAtomTextPartSchema),
  bbox: BoundingBoxSchema,
  shift: VectorSchema,
});

const DrawnBondDataSchema = z.object({
  cycle: z.array(z.string()).optional(),
});

const DrawnBondCenterSchema = z.object({
  type: z.string(),
});

const DrawnAtomSchema = z.object({
  idx: z.string(),
  data: DrawnAtomDataSchema,
  center: z.object({}).optional(),
  centerLabel: DrawnAtomCenterLabelSchema.optional(),
  side: z.object({}).optional(),
  sideLabel: DrawnAtomSideLabelSchema.optional(),
  bbox: BoundingBoxSchema,
  pos: VectorSchema,
  color: z.string(),
  warnings: z.array(z.string()),
  selected: z.boolean().optional(),
});

const DrawnBondSchema = z.object({
  idx: z.string(),
  start: z.string(),
  end: z.string(),
  data: DrawnBondDataSchema,
  center: DrawnBondCenterSchema,
  left: z.object({}),
  right: z.object({}),
  warnings: z.array(z.string()),
  cut: z.boolean().optional(),
  color: z.string().optional(),
});

export const DrawnMolSchema = z.object({
  atoms: z.record(DrawnAtomSchema),
  bonds: z.record(DrawnBondSchema),
  bbox: BoundingBoxSchema,
});
export type DrawnMol = z.infer<typeof DrawnMolSchema>;

export type PriceRange = [number | undefined, number | undefined];

export const ElementsSchema = z.object({
  'reaction': z.boolean(),
  'synthesis-tree': z.boolean(),
});

export type Elements = z.infer<typeof ElementsSchema>;
